import React, { memo, useCallback } from 'react'
import classNames from 'classnames'
import { ISelectOption } from 'components/forms/Selects/SelectOption'
import { FunctionalTag } from 'transfix-ui/components/FunctionalTag'
import { TAG_SIZE } from 'transfix-ui/components/Tag'
import { Toast, useToastState } from 'transfix-ui/components/Toast'
import { makeStyles } from 'transfix-ui/core'
import Typography from 'transfix-ui/core/Typography'
import { IShipperRateSourceStatusEnum } from 'types/graphqlTypes'
import { jobStatusOptions } from 'apps/marketplace/utils/constants/shipperRates'
import { useUpdateShipperRateSourceStatusMutation } from './graphql/UpdateShipperRateSourceStatus'

export const TOAST_MESSAGE = {
  success: 'Successfully updated job status.',
  failure: 'Failed to update job status: ',
}

const useSourceStatusSelectorStyles = makeStyles({
  header: {
    marginBottom: 5,
  },
})

interface ISourceStatusSelectorProps {
  awardedLaneId?: string
  loading?: boolean
  sourceStatus?: IShipperRateSourceStatusEnum
  onChange?: (sourceStatus: IShipperRateSourceStatusEnum) => void
}

const SourceStatusSelector = memo<ISourceStatusSelectorProps>(
  ({ awardedLaneId, loading = false, onChange, sourceStatus }) => {
    const classes = useSourceStatusSelectorStyles()

    const { closeToast, openToast, toastMessage, toastOpen } = useToastState()

    const [
      updateSourceStatus,
      { loading: updateLoading },
    ] = useUpdateShipperRateSourceStatusMutation()

    const handleSourceStatusSelect = useCallback(
      async (option: ISelectOption<string>) => {
        const selectedSourceStatus = option?.value as IShipperRateSourceStatusEnum

        if (awardedLaneId && selectedSourceStatus !== sourceStatus) {
          try {
            let errorMessage: string | null = null
            const { data } = await updateSourceStatus({
              variables: {
                id: awardedLaneId,
                sourceStatus: selectedSourceStatus,
              },
            })

            if (data?.updateShipperRate?.errors) {
              errorMessage = data.updateShipperRate.errors.map(err => err.message).join('\n')
            }

            if (errorMessage) {
              openToast({
                toastMessage: `${TOAST_MESSAGE.failure}\n${errorMessage}`,
                toastType: 'alert',
              })
            } else {
              onChange?.(selectedSourceStatus)
              openToast({
                toastMessage: TOAST_MESSAGE.success,
                toastType: 'default',
              })
            }
          } catch (err) {
            openToast({
              toastMessage: `${TOAST_MESSAGE.failure}\n${err.message}`,
              toastType: 'alert',
            })
          }
        }
      },
      [awardedLaneId, sourceStatus, onChange, openToast, updateSourceStatus]
    )

    return (
      <div>
        <Typography
          className={classNames(classes.header, 'text-secondary')}
          display='block'
          variant='subtitle1'>
          Status
        </Typography>

        <FunctionalTag
          options={jobStatusOptions}
          value={sourceStatus ?? ''}
          onSelect={handleSourceStatusSelect}
          size={TAG_SIZE.small}
          isLoading={loading || updateLoading}
          dataTest='job-status-functional-tag'
        />

        <Toast message={toastMessage} open={toastOpen} onClose={closeToast} />
      </div>
    )
  }
)

export default SourceStatusSelector
